import React, { ChangeEvent, Fragment } from "react";

import { DateTime } from "luxon";

import DayTimeline from "../DayTimeline";
import DelayInput from "../DelayInput";

import { V2 } from "../../Api";
import { formatDateLocalDayOfWeek, formatName } from "../../Utilities";
import withTitle from "../withTitle";
import withAuth from "../withAuth";
import { IAuthProps, WellKnownGroups } from "../AuthContext";
import { RouteComponentPropsHistory } from "../../types";
import withProps from "../withProps";

interface IScheduleCoverageDetailsViewState {
    events: any;
    isLoading: boolean | string;

    canEditCalendar: boolean;

    startDate: string;
    endDate: string;

    searchCoverage: string;

    calCoverage: string;
    calItem: any;

    calLength: string;
    calLengthType: string;
    calRepeat: boolean;
    calRepeatType: string;
    calRepeatLength: string;
    calHideRepeat: boolean;
    calOpen: string;
    calClose: string;

    calSundayEnabled: boolean;
    calSundayOpen: string;
    calSundayClose: string;

    calMondayEnabled: boolean;
    calMondayOpen: string;
    calMondayClose: string;

    calTuesdayEnabled: boolean;
    calTuesdayOpen: string;
    calTuesdayClose: string;

    calWednesdayEnabled: boolean;
    calWednesdayOpen: string;
    calWednesdayClose: string;

    calThursdayEnabled: boolean;
    calThursdayOpen: string;
    calThursdayClose: string;

    calFridayEnabled: boolean;
    calFridayOpen: string;
    calFridayClose: string;

    calSaturdayEnabled: boolean;
    calSaturdayOpen: string;
    calSaturdayClose: string;

    employees: any[];
    coverages: any[];

    dayDetailsEntries: any[];
}

interface IScheduleCoverageDetailsView extends IAuthProps, RouteComponentPropsHistory<any, any> {
    group: string;
}

class ScheduleCoverageDetailsView extends React.Component<IScheduleCoverageDetailsView, IScheduleCoverageDetailsViewState> {
    constructor(props: Readonly<IScheduleCoverageDetailsView>) {
        super(props);

        const date = (DateTime.fromISO(props.match.params.date) || DateTime.local()).plus({ days: 1 });

        const state: IScheduleCoverageDetailsViewState = {
            events: {},
            isLoading: false,

            canEditCalendar: props.authContext.hasRight("sched:off:write"),

            endDate: date.endOf("week").plus({ days: -1 }).toISODate(),
            startDate: date.startOf("week").plus({ days: -1 }).toISODate(),

            searchCoverage: (props.location.state && props.location.state.searchCoverage) || "",

            calLength: "",
            calLengthType: "1",
            calCoverage: "",
            calRepeat: false,
            calRepeatType: "week",
            calRepeatLength: "1",
            calItem: {},
            calHideRepeat: false,
            calOpen: "",
            calClose: "",

            employees: [],
            coverages: [],

            calSundayEnabled: false,
            calSundayOpen: "",
            calSundayClose: "",

            calMondayEnabled: false,
            calMondayOpen: "",
            calMondayClose: "",

            calTuesdayEnabled: false,
            calTuesdayOpen: "",
            calTuesdayClose: "",

            calWednesdayEnabled: false,
            calWednesdayOpen: "",
            calWednesdayClose: "",

            calThursdayEnabled: false,
            calThursdayOpen: "",
            calThursdayClose: "",

            calFridayEnabled: false,
            calFridayOpen: "",
            calFridayClose: "",

            calSaturdayEnabled: false,
            calSaturdayOpen: "",
            calSaturdayClose: "",

            dayDetailsEntries: [],
        };

        this.state = state;

        this.handleElementChange = this.handleElementChange.bind(this);
        this.handleDialogChange = this.handleDialogChange.bind(this);
    }

    public async componentDidMount() {
        try {
            const coverages = await V2.get<any[]>(`schedule/coverage/types?group=${this.props.group}`);

            this.setState({
                coverages
            });
        }
        catch (e) {
            //
        }

        await this.loadData();
    }

    public render() {
        return (
            <div className="viewer-root">
                <div className="filter-control-box">
                    <span className="control-group fill-space">
                        <span>Start: </span>
                        <DelayInput type="date" timeout={500} value={this.state.startDate} name="startDate" onChangeDelayed={this.handleElementChange} />
                    </span>
                    <span className="control-group fill-space">
                        <span>End: </span>
                        <DelayInput type="date" timeout={500} value={this.state.endDate} name="endDate" onChangeDelayed={this.handleElementChange} />
                    </span>
                    <select value={this.state.searchCoverage} name="searchCoverage" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Coverages</option>
                        {this.state.coverages.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                </div>
                <div>
                    {this.state.isLoading === true ?
                        <div className="spinner spinner-center lds-dual-ring-big" />
                        : typeof this.state.isLoading === "string"
                            ? <span className="error-text">{this.state.isLoading}</span>
                            : this.state.dayDetailsEntries.map(a =>
                                <Fragment>
                                    <h3 className="underline-light">{formatDateLocalDayOfWeek(a.date)}</h3>
                                    {a.entries.length > 0 ? <DayTimeline entries={a.entries} /> : <em>Nothing scheduled</em>}
                                </Fragment>
                            )}
                </div>
            </div>
        );
    }

    private async loadData() {
        try {
            this.setState({
                events: {},
                isLoading: true,
            });

            const beginDate = DateTime.fromISO(this.state.startDate);
            let endDate = DateTime.fromISO(this.state.endDate).plus({ millisecond: 1 });
            let totalDays = endDate.diff(beginDate, "days").days;
            if (totalDays < 1) {
                totalDays = 1;
            }
            else if (totalDays > 7) {
                endDate = beginDate.plus({ days: 7 });
                totalDays = 7;
            }

            const parameters = [
                `group=${this.props.group}`,
                `after=${encodeURIComponent(beginDate.toISO())}`,
                `before=${encodeURIComponent(endDate.toISO())}`
            ];

            if (this.state.searchCoverage) {
                parameters.push(`coverage=${this.state.searchCoverage}`);
            }

            const data = await V2.get<any[]>(`schedule/coverage?${parameters.join("&")}`);

            // Order by coverage, then date
            data.sort((a, b) => a.coverage.id.localeCompare(b.coverage.id) || a.date.localeCompare(b.date));


            const dayDetails: { [item: number]: any } = {};
            for (let day = 0; day < totalDays; day++) {
                const date = beginDate.plus({ days: day });
                dayDetails[day + (beginDate.weekday % 7)] = { date, entries: [] };
            }


            let prevItem = null;
            for (let i = 0; i < data.length; i++) {
                const item = data[i];
                const date = DateTime.fromISO(item.date);
                const day = date.weekday % 7;

                const time = DateTime.fromISO(item.time).toObject();
                let start = date.plus({ hours: time.hour, minutes: time.minute });
                let end = start.plus({ hours: item.length });

                if (!dayDetails[day]) {
                    dayDetails[day] = {
                        entries: []
                    };
                }

                const runs = [];

                if (prevItem) {
                    runs.unshift({
                        id: "-1",
                        start: date.set({ hour: 0, minute: 0 }),
                        end: prevItem.end,
                        text: prevItem.employee ? `<--` : undefined,
                        color: prevItem.employee ? "green" : "red"
                    });
                    prevItem = null;
                }

                if (!end.hasSame(start, "day")) {
                    // Wraps to next day
                    prevItem = item;
                    prevItem.end = end;
                    end = date.set({ hour: 24, minute: 0 });
                }

                runs.push({
                    id: "0",
                    start,
                    end,
                    text: item.employee ? `[${formatName(item.employee.name, true)}]` : undefined,
                    color: item.employee ? "green" : "red"
                });

                dayDetails[day].entries.push({
                    id: item.coverage.id,
                    name: item.coverage.name,
                    runs
                });
            }
            this.setState({
                dayDetailsEntries: Object.keys(dayDetails).map(a => dayDetails[+a]),
                isLoading: false
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Unable to load schedule"
            });
        }
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        let value = e.currentTarget.value;
        const name = e.currentTarget.name;

        if ((name === "startDate" || name === "endDate") && !value) {
            return;
        }

        const newState: Partial<IScheduleCoverageDetailsViewState> = {};

        if (name === "endDate") {
            const startDate = this.state.startDate && DateTime.fromISO(this.state.startDate);
            if (startDate && !(DateTime.fromISO(value).plus({ day: 1 })).hasSame(startDate.plus({ day: 1 }), "week")) {
                // Cap end date to end of week
                value = startDate.plus({ day: 1 }).endOf("week").minus({ days: 1 }).toISODate();
            }
        }

        if (name === "startDate") {
            const endDate = this.state.endDate && DateTime.fromISO(this.state.endDate);
            const startDate = DateTime.fromISO(value);
            if (endDate && endDate.diff(startDate, "days").days < 0) {
                // Bump end date to end of week
                newState.endDate = startDate.plus({ days: 1 }).set({ weekday: 6 }).toISODate();
            }
        }

        (newState as any)[name] = value;

        this.setState(newState as any, () => {
            // this.saveSearchState();
            this.loadData();
        });
    }

    private handleDialogChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            [name]: value
        } as any);
    }
}

export default withTitle("Coverage Week Details", withProps(withAuth(ScheduleCoverageDetailsView), { group: WellKnownGroups.Surgeon }));
export const ScheduleTelemarketerCallDetailsView = withTitle("Patient Care Rep. On-Call Week Details", withProps(withAuth(ScheduleCoverageDetailsView), { group: WellKnownGroups.PatientCareRep }));
export const ScheduleSurgicalConsultCallDetailsView = withTitle("Surgery Consultant On-Call Week Details", withProps(withAuth(ScheduleCoverageDetailsView), { group: WellKnownGroups.SurgicalConsultant }));
